// queries — the react-query hooks the Field screens share.
//
// Every read goes through loadWithCache: a fresh answer is remembered, a
// NetworkError falls back to the remembered one with `__stale` set, and a
// refusal is thrown so the screen shows the server's sentence. Keys all
// start with 'field' so the offline provider can invalidate the lot after a
// replay lands.

import { useQuery } from '@tanstack/react-query';
import { get } from '../shared/http';
import { loadWithCache } from './offline/cache';
import { dayEndPath } from './dayEnd';

export const TODAY_KEY = ['field', 'today'];
export const jobKey = (id) => ['field', 'job', String(id)];
export const hoursKey = (week) => ['field', 'hours', week || 'current'];

export function useToday(options = {}) {
  return useQuery({
    queryKey: TODAY_KEY,
    queryFn: () => loadWithCache('today', () => get('/field/today')),
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
    ...options,
  });
}

export function useJob(id, options = {}) {
  return useQuery({
    queryKey: jobKey(id),
    queryFn: () => loadWithCache(`job:${id}`, () => get(`/field/job/${id}`)),
    refetchOnWindowFocus: true,
    retry: false,
    enabled: !!id,
    ...options,
  });
}

export function useHours(week, options = {}) {
  return useQuery({
    queryKey: hoursKey(week),
    queryFn: () => get(`/field/hours${week ? `?week=${encodeURIComponent(week)}` : ''}`),
    refetchOnWindowFocus: true,
    retry: false,
    ...options,
  });
}

export const dayEndKey = (scope) => ['field', 'day-end', scope];

/**
 * The end-of-day review, asked for only while the sheet is open.
 *
 * NOT through loadWithCache, and that is the point: every other Field read
 * falls back to the last good answer with an `__stale` flag so a phone with
 * no signal still shows the day. A remembered review is a list of promises
 * about what pressing a button will do, and the button cannot be pressed
 * without a connection anyway (the confirm is a live POST). Showing a
 * cached one would offer to close jobs from a plan the server never agreed
 * to — the same reason apps/field/actions.js refuses to queue a claim.
 *
 * `staleTime: 0` and no polling: it is fetched when the sheet opens and
 * again after every confirm, which is exactly when it can have changed.
 */
export function useDayEndPlan(scope, options = {}) {
  return useQuery({
    queryKey: dayEndKey(scope),
    queryFn: () => get(dayEndPath(scope)),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
}

export const PAYSTUBS_KEY = ['field', 'paystubs'];

/**
 * The worker's own year: what they were paid and a rough tax set-aside,
 * summed by the server (GET /paystubs/my-year, utils/form1099.js workerYear —
 * the step the 1099 screen reads). `year` null asks for this year.
 */
export const payYearKey = (year) => ['field', 'pay-year', year || 'this'];
export function usePayYear(year, options = {}) {
  return useQuery({
    queryKey: payYearKey(year),
    queryFn: () => get(year ? `/paystubs/my-year?year=${encodeURIComponent(year)}` : '/paystubs/my-year'),
    // The year shown stays up while the next one loads. Without it, picking a
    // year emptied `data`, the block — year picker included — was swapped for
    // a skeleton, and the dropdown vanished under the thumb that had just
    // used it (third review of #739). A failure still withholds every figure:
    // placeholder data lasts only while the request is pending.
    placeholderData: (previous) => previous,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
}

/** A worker's own paystubs — the server limits a worker to their own rows. */
export function usePaystubs(options = {}) {
  return useQuery({
    queryKey: PAYSTUBS_KEY,
    queryFn: () => get('/paystubs'),
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });
}
